import { useEffect, useState } from "react";
import { formatPhoneNumberForDisplay } from "../../../../util/helpers";
import { conversationName as knownName } from "../../../../util/whatsappProfile";

// Helpers for the chat inbox list: who a conversation is with, its channel, whether the customer is waiting
// for an answer, and the search over the loaded conversations.

const CHANNELS = ['whatsapp', 'sms', 'mms'];

// Saved contact name, else the WhatsApp profile name, else the number.
export const conversationName = conversation =>
    knownName(conversation) || formatPhoneNumberForDisplay(conversation?.clientNumber) || '';

export const hasContactName = conversation => !!knownName(conversation);

// The channel of the last real message (system notes and whispers carry the platform too, or nothing).
export const conversationChannel = conversation => {
    const last = conversation?.lastMessage;
    if (!last) return null;
    if (CHANNELS.includes(last.platform)) return last.platform;
    if (CHANNELS.includes(last.channel)) return last.channel;
    return null;
};

// The last message came from the customer: they are waiting for an answer.
export const isInbound = conversation => {
    const last = conversation?.lastMessage;
    if (!last || ['system', 'note', 'whisper', 'date'].includes(last.channel)) return false;
    if (last.direction) return last.direction === 'in';
    return !!last.from && last.from === conversation.clientNumber;
};

export const waitingMs = (conversation, now = Date.now()) => {
    if (!isInbound(conversation)) return null;
    const at = new Date(conversation.lastMessage.time).getTime();
    return Number.isFinite(at) ? Math.max(0, now - at) : null;
};

export const waitLevel = ms => (ms === null ? null : ms >= 15 * 60000 ? 'long' : ms >= 5 * 60000 ? 'mid' : 'short');

export const formatWait = (intl, ms) => {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return intl.formatMessage({id: 'chats.wait.lessMinute', defaultMessage: '<1 min'});
    if (minutes < 60) return intl.formatMessage({id: 'chats.wait.minutes', defaultMessage: '{n} min'}, {n: minutes});
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return intl.formatMessage({id: 'chats.wait.hours', defaultMessage: '{h} h {m} min'}, {h: hours, m: minutes % 60});
    return intl.formatMessage({id: 'chats.wait.days', defaultMessage: '{n} d'}, {n: Math.floor(hours / 24)});
};

const lastTime = conversation => new Date(conversation?.lastMessage?.time || 0).getTime() || 0;

// Newest activity first; conversations without messages (just created) on top.
export const newestFirst = list => list.slice().sort((a, b) => {
    if (!a.lastMessage) return -1;
    if (!b.lastMessage) return 1;
    return lastTime(b) - lastTime(a);
});

// Longest waiting first (the queue order).
export const oldestWaitingFirst = (list, now = Date.now()) => list.slice().sort((a, b) => {
    const wa = waitingMs(a, now);
    const wb = waitingMs(b, now);
    if (wa !== null && wb !== null) return wb - wa;
    if (wa !== null) return -1;
    if (wb !== null) return 1;
    return lastTime(b) - lastTime(a);
});

const digits = value => String(value || '').replace(/\D/g, '');

export const matchesSearch = (conversation, query) => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return true;
    if (conversationName(conversation).toLowerCase().includes(q)) return true;
    const qDigits = digits(q);
    if (qDigits.length >= 3 && digits(conversation.clientNumber).includes(qDigits)) return true;
    const body = conversation?.lastMessage?.channel === 'system' ? '' : conversation?.lastMessage?.body;
    return String(body || '').toLowerCase().includes(q);
};

// Re-renders every `ms` (waiting times).
export const useNow = (ms = 30000) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), ms);
        return () => clearInterval(timer);
    }, [ms]);
    return now;
};

// Soft avatar colours (dark text), picked by the number so a person keeps their colour.
const AVATAR_COLOURS = ['#fdba74', '#93c5fd', '#c4b5fd', '#86efac', '#f9a8d4', '#fcd34d', '#a5b4fc', '#67e8f9'];
export const avatarColour = number => AVATAR_COLOURS[Number(String(number || '0').slice(-1)) % AVATAR_COLOURS.length];

// Letters and digits only: a sender like "<SENDER_ID>" or "sender_wa_id" must not give "D>" or "d_".
export const initialsOf = conversation => {
    if (!hasContactName(conversation)) {
        const raw = String(conversation?.clientNumber || '');
        const numberDigits = raw.replace(/\D/g, '');
        if (numberDigits.length >= 2) return numberDigits.slice(-2);
        const letters = raw.replace(/[^\p{L}\p{N}]/gu, '');
        return letters ? letters.slice(0, 2).toUpperCase() : '?';
    }
    const words = conversationName(conversation).split(/\s+/).map(part => part.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
    return words.length ? words.slice(0, 2).map(part => part[0]).join('').toUpperCase() : '?';
};
